import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './TokenCreateModal.css';

const TokenCreateModal = ({ 
  isOpen, 
  onDismiss, 
  tokenName,
  setTokenName,
  expirationDays,
  setExpirationDays,
  onSubmit,
  isSubmitting, 
  triggerRef 
}) => {
  const [position, setPosition] = useState({ top: 60, right: 20 });
  const popoverRef = useRef(null);

  // Update position based on trigger button
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, triggerRef]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const trigger = triggerRef?.current;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        (!trigger || !trigger.contains(e.target))
      ) {
        onDismiss();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onDismiss, triggerRef]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      onSubmit(e);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isSubmitting) {
      onSubmit(e);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="token-create-overlay">
      <div
        ref={popoverRef}
        className="token-create-popover"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          right: `${position.right}px`,
        }}
      >
        <div className="token-input-wrapper">
          <input
            type="text"
            value={tokenName}
            onChange={(e) => setTokenName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Token name (e.g., GitHub Actions CI)"
            className="token-input"
            disabled={isSubmitting}
            maxLength={100}
            autoFocus
          />
          <select
            value={expirationDays}
            onChange={(e) => setExpirationDays(Number(e.target.value))}
            className="token-select"
            disabled={isSubmitting}
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={0}>Never</option>
          </select>
          <button
            onClick={handleSubmit}
            disabled={!tokenName.trim() || isSubmitting}
            className="token-btn"
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TokenCreateModal;
